<template>
  <div>
    <div class="tw-flex tw-justify-between tw-items-center">
      <base-text-field v-model="query" ref="searchInput" class="tw-flex-grow" placeholder="Search for a rx item" size="sm" tabindex="1">
        <template #prefix>
          <search-icon class="tw-h-5 tw-w-5" />
        </template>
      </base-text-field>
      <base-button class="tw-ml-5 tw-hidden sm:tw-flex" type="primary" @click="add">
        <template v-slot:icon>
          <plus-icon />
        </template>
        Add Item
      </base-button>
      <base-icon @click="add" class="tw-ml-3 tw-inline sm:tw-hidden">
        <plus-icon color="green" class="tw-h-5 tw-w-5" />
      </base-icon>
    </div>
    <base-table :loading="isLoading" :fields="fields" :items="items" class="tw-mt-5">
      <template #product="{item: product}">
        <div>{{ product.name }}</div>
        <div class="tw-text-gray-500 tw-text-xs">{{ product.productGroup }}</div>
      </template>
      <template #actions="{item: product}">
        <div class="tw-flex tw-items-center tw-space-x-2">
          <base-button type="flat" @click="edit(product)">
            <template v-slot:icon>
              <edit-icon /> </template
            >Edit
          </base-button>
          <base-button type="flat" @click="del(product)">
            <template v-slot:icon>
              <trash-icon /> </template
            >Delete
          </base-button>
        </div>
      </template>
      <template #empty-state>
        <div class="tw-flex tw-justify-center tw-py-5">
          No Results
        </div>
      </template>
      <template #loading-row>
        <td class="tw-py-3 tw-px-4">
          <div class="tw-flex tw-flex-col tw-w-full">
            <base-skeleton-loader class="tw-h-4 tw-w-full tw-mb-2" />
            <base-skeleton-loader class="tw-h-3 tw-w-16" />
          </div>
        </td>
        <td class="tw-py-3 tw-px-4">
          <div class="tw-flex tw-space-x-3 tw-justify-center">
            <base-skeleton-loader class="tw-h-6 tw-w-12" />
            <base-skeleton-loader class="tw-h-6 tw-w-12" />
          </div>
        </td>
      </template>
    </base-table>
    <base-pagination v-model="page" class="tw-mt-2" />
    <business-rx-item-slideover ref="slideover" @save="loadPage" />
    <business-rx-item-delete-confirmation-modal ref="deleteConfirmationModal" @delete="loadPage" />
  </div>
</template>

<script>
import BusinessRxItemSlideover from "@/views/business/business-rx-items/components/BusinessRxItemSlideover";
import BusinessRxItemDeleteConfirmationModal from "@/views/business/business-rx-items/components/BusinessRxItemDeleteConfirmationModal";
import { PlusIcon, EditIcon, TrashIcon, SearchIcon } from "@/assets/svgs";
import { debounce } from "debounce";

const TAKE_KEY = "predictile_business-rx-items_take";

export default {
  name: "BusinessRxItems",
  components: {
    BusinessRxItemSlideover,
    BusinessRxItemDeleteConfirmationModal,
    PlusIcon,
    EditIcon,
    TrashIcon,
    SearchIcon,
  },
  props: {
    businessId: String,
  },
  data() {
    return {
      items: [],
      page: {
        take: localStorage.getItem(TAKE_KEY) ? +localStorage.getItem(TAKE_KEY) : 50,
        skip: 0,
        total: 0,
      },
      fields: [{ title: "Rx Item", key: "product", thClass: "" }, { title: "Actions", key: "actions", thClass: "tw-w-0 tw-text-center" }],
      query: null,
      isLoading: false,
      productGroups: [],
      programs: [],
    };
  },
  computed: {},
  async created() {
    await this.loadPage();
    this.debouncedLoadPage = debounce(this.loadPage, 500);
  },
  mounted() {
    const searchInputRef = this.$refs.searchInput;
    searchInputRef.focus();
  },
  watch: {
    page(newValue) {
      localStorage.setItem(TAKE_KEY, newValue.take);
      this.loadPage();
    },
    query() {
      this.page.skip = 0;
      this.debouncedLoadPage();
    },
  },
  methods: {
    add() {
      this.$refs.slideover.show(this.businessId, null, this.productGroups, this.programs);
    },
    edit(item) {
      this.$refs.slideover.show(this.businessId, item, this.productGroups, this.programs);
    },
    del(item) {
      this.$refs.deleteConfirmationModal.show(this.businessId, item.businessItemCategoryId, item.name);
    },
    next() {
      this.page.skip += this.page.take;
    },
    previous() {
      this.page.skip -= this.page.take;
    },
    async loadPage() {
      this.isLoading = true;

      await this.getPrograms();

      let url = `/businesses/${this.businessId}/item-categories`;
      url += `?skip=${this.page.skip}&take=${this.page.take}`;
      url += `&type=rx_item`;
      if (this.query) url += `&query=${encodeURIComponent(this.query)}`;

      this.$axios
        .get(url)
        .then((res) => {
          this.page.total = res.data.total;
          this.items = res.data.items;
          this.productGroups = this.getGroups(res.data.items);
        })
        .catch((err) => {
          this.EventBus.$emit("GLOBAL_ERROR", {
            title: `Failed to load rx-items`,
            message: `We had a problem retrieving the list of rx-items. Please reload the page to try again.`,
            error: err,
          });
        })
        .finally(() => {
          this.isLoading = false;
        });
    },
    getGroups(items) {
      let groups = [...new Map(items.map((item) => [item["productGroup"], { value: item["productGroup"], label: item["productGroup"] }])).values()].filter((x) => x.value !== null);
      groups.push({ value: "new group", label: "+ Add New Group" });
      return groups;
    },
    async getPrograms() {
      try {
        const response = await this.$axios.get(`/businesses/${this.businessId}/programs/list?includeDeleted=false`);
        this.programs = response.data;
      } catch (error) {
        this.EventBus.$emit("GLOBAL_ERROR", {
          title: "Unable to retrieve programs",
          message: "We were not able to retrieve your business programs. There may have been a connection error. Please reload the page to try again.",
          error,
        });
      }
    },
  },
};
</script>
